import type { ForwardedRef } from "react";
import { forwardRef, useDeferredValue, useEffect, useMemo, useState } from "react";

import Icon from "common/components/icons";
import { ConversationMessage } from "common/types/common.type";
import { useChatContext } from "pages/chat/context/chat";
import useScrollToBottom from "./hooks/useScrollToBottom";
import {
  CallIcon,
  CallMessage,
  ChatMessage,
  ChatMessageFooter,
  ContactCard,
  ContactCardAvatar,
  ContactCardBody,
  ContactCardFooter,
  ContactCardIcon,
  ContactCardLabel,
  ContactCardName,
  ContactCardText,
  Container,
  Date,
  DateWrapper,
  EmptyState,
  EmptyStateText,
  EncryptionMessage,
  ForwardedIcon,
  ForwardedLabel,
  InfoMessage,
  InfoPreviewBody,
  InfoPreviewCard,
  InfoPreviewDescription,
  InfoPreviewImage,
  InfoPreviewLink,
  InfoPreviewSite,
  InfoPreviewTitle,
  InfoText,
  MessageGroup,
  MessagePreviewBody,
  MessagePreviewCard,
  MessagePreviewDescription,
  MessagePreviewImage,
  MessagePreviewLink,
  MessagePreviewSite,
  MessagePreviewTitle,
  StickyDate,
} from "./styles";

type MessagesListProps = {
  onShowBottomIcon: (show: boolean) => void;
  shouldScrollToBottom?: boolean;
};

export default function MessagesList(props: MessagesListProps) {
  const { onShowBottomIcon, shouldScrollToBottom } = props;
  const chatCtx = useChatContext();
  const activeChat = chatCtx.activeChat;
  const deferredTopics = useDeferredValue(chatCtx.selectedTopics);

  const messages = useMemo(() => {
    const allMessages = activeChat?.messages || [];

    if (deferredTopics.length === 0) {
      return allMessages;
    }

    const filteredMessages: ConversationMessage[] = [];
    let pendingDate: ConversationMessage | undefined;

    for (const message of allMessages) {
      if (message.type === "date") {
        pendingDate = message;
        continue;
      }

      if (!message.topic || !deferredTopics.includes(message.topic)) {
        continue;
      }

      if (pendingDate) {
        filteredMessages.push(pendingDate);
        pendingDate = undefined;
      }

      filteredMessages.push(message);
    }

    return filteredMessages;
  }, [activeChat?.messages, deferredTopics]);
  const transitionKey = useMemo(
    () => `${activeChat?.id || "no-chat"}:${deferredTopics.join("|") || "all"}`,
    [activeChat?.id, deferredTopics]
  );

  const [stickyDate, setStickyDate] = useState<string>("");
  const { containerRef, lastMessageRef } = useScrollToBottom(
    onShowBottomIcon,
    shouldScrollToBottom,
    activeChat?.id
  );

  useEffect(() => {
    const container = containerRef.current as HTMLDivElement | null;
    if (!container) {
      return;
    }

    const updateStickyDate = () => {
      const dateNodes = Array.from(
        container.querySelectorAll<HTMLDivElement>("[data-date-label]")
      );

      let current = "";
      for (const node of dateNodes) {
        const offset = node.offsetTop - container.scrollTop;
        if (offset <= 4) {
          current = node.dataset.dateLabel || current;
        } else {
          break;
        }
      }

      if (!current && dateNodes.length > 0) {
        current = dateNodes[0].dataset.dateLabel || "";
      }

      setStickyDate(current);
    };

    updateStickyDate();
    container.addEventListener("scroll", updateStickyDate, { passive: true });

    return () => {
      container.removeEventListener("scroll", updateStickyDate);
    };
  }, [activeChat?.id, messages, containerRef]);

  return (
    <Container ref={containerRef}>
      {stickyDate && (
        <StickyDate>
          <Date>{stickyDate}</Date>
        </StickyDate>
      )}
      <EncryptionMessage>
        <Icon id="lock" className="icon" />
        ההודעות מוצפנות מקצה לקצה. אף אחד מחוץ לשיחה הזו, כולל WhatsApp, לא יכול לקרוא או
        להאזין להן.
      </EncryptionMessage>
      {activeChat?.context && <InfoMessage>{activeChat.context}</InfoMessage>}
      {messages.length === 0 && (
        <EmptyState key={`empty-${transitionKey}`}>
          <EmptyStateText>אין הודעות שמתאימות לסינון שבחרת.</EmptyStateText>
          <EmptyStateText>אפשר לבחור כמה נושאים יחד או ללחוץ על &quot;הצג הכל&quot;.</EmptyStateText>
        </EmptyState>
      )}
      <MessageGroup key={transitionKey}>
        {messages.map((message, i) => {
          if (message.type === "info") {
            return (
              <InfoMessage key={message.id}>
                <InfoText>{renderMarkdownLinks(message.text)}</InfoText>
                {message.linkPreview && (
                  <InfoPreviewLink
                    href={message.linkPreview.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <InfoPreviewCard>
                      {message.linkPreview.image && (
                        <InfoPreviewImage
                          src={message.linkPreview.image}
                          alt={message.linkPreview.title || "Preview image"}
                        />
                      )}
                      <InfoPreviewBody>
                        {message.linkPreview.title && (
                          <InfoPreviewTitle>{message.linkPreview.title}</InfoPreviewTitle>
                        )}
                        {message.linkPreview.description && (
                          <InfoPreviewDescription>
                            {message.linkPreview.description}
                          </InfoPreviewDescription>
                        )}
                        <InfoPreviewSite>
                          {message.linkPreview.site || message.linkPreview.url}
                        </InfoPreviewSite>
                      </InfoPreviewBody>
                    </InfoPreviewCard>
                  </InfoPreviewLink>
                )}
              </InfoMessage>
            );
          }

          if (message.type === "call") {
            const label = message.text || "שיחת קול";
            const duration = message.callDuration ? ` · ${message.callDuration}` : "";

            return (
              <CallMessage key={message.id}>
                <CallIcon>
                  <Icon id="call" />
                </CallIcon>
                <span>
                  {label}
                  {duration}
                </span>
              </CallMessage>
            );
          }

          if (message.type === "date") {
            return (
              <DateWrapper key={message.id} data-date-label={message.text}>
                <Date>{message.text}</Date>
              </DateWrapper>
            );
          }

          const nextMessage = messages[i + 1];
          const isOpponent = message.from !== "me";
          const nextIsSameSide =
            nextMessage &&
            nextMessage.type !== "info" &&
            (nextMessage.from !== "me") === isOpponent;
          const hasTail = !nextIsSameSide;

          if (i === messages.length - 1) {
            return (
              <SingleMessage
                key={message.id}
                message={message}
                ref={lastMessageRef}
                hasTail={hasTail}
              />
            );
          }

          return <SingleMessage key={message.id} message={message} hasTail={hasTail} />;
        })}
      </MessageGroup>
    </Container>
  );
}

const LINK_REGEX = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;

const renderMarkdownLinks = (text: string) => {
  const parts: Array<string | JSX.Element> = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = LINK_REGEX.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const label = match[1];
    const url = match[2];

    parts.push(
      <a key={`link-${match.index}`} href={url} target="_blank" rel="noreferrer">
        {label}
      </a>
    );

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts.length ? parts : text;
};

type SingleMessageProps = {
  message: ConversationMessage;
  hasTail: boolean;
};

  const SingleMessage = forwardRef(function SingleMessage(
  props: SingleMessageProps,
  ref: ForwardedRef<HTMLDivElement>
) {
  const { message, hasTail } = props;
  const isOpponent = message.from !== "me";

  return (
    <ChatMessage
      className={`${isOpponent ? "chat__msg--received" : "chat__msg--sent"} ${
        hasTail ? "has-tail" : "is-grouped"
      }`}
      ref={ref}
    >
      {message.forwarded && (
        <ForwardedLabel>
          <ForwardedIcon src="/assets/images/forward.png" alt="" />
          הועבר
          {message.forwardedFrom ? ` מ־${message.forwardedFrom}` : ""}
        </ForwardedLabel>
      )}
      {message.quoted && <ForwardedLabel>מצטט הודעה</ForwardedLabel>}
      {message.type === "contact" && message.contactCard ? (
        <ContactCard>
          <ContactCardBody>
            {message.contactCard.image ? (
              <ContactCardAvatar
                src={message.contactCard.image}
                alt={message.contactCard.name}
              />
            ) : (
              <ContactCardIcon>
                <Icon id="attachContacts" />
              </ContactCardIcon>
            )}
            <ContactCardText>
              <ContactCardName>{message.contactCard.name}</ContactCardName>
              {message.contactCard.label ? (
                <ContactCardLabel>{message.contactCard.label}</ContactCardLabel>
              ) : null}
            </ContactCardText>
          </ContactCardBody>
          <ContactCardFooter>איש קשר</ContactCardFooter>
        </ContactCard>
      ) : (
        <>
          <span>{message.text}</span>
          {message.linkPreview && (
            <MessagePreviewLink
              href={message.linkPreview.url}
              target="_blank"
              rel="noreferrer"
            >
              <MessagePreviewCard>
                {message.linkPreview.image && (
                  <MessagePreviewImage
                    src={message.linkPreview.image}
                    alt={message.linkPreview.title || "Preview image"}
                  />
                )}
                <MessagePreviewBody>
                  {message.linkPreview.title && (
                    <MessagePreviewTitle>{message.linkPreview.title}</MessagePreviewTitle>
                  )}
                  {message.linkPreview.description && (
                    <MessagePreviewDescription>
                      {message.linkPreview.description}
                    </MessagePreviewDescription>
                  )}
                  <MessagePreviewSite>
                    {message.linkPreview.site || message.linkPreview.url}
                  </MessagePreviewSite>
                </MessagePreviewBody>
              </MessagePreviewCard>
            </MessagePreviewLink>
          )}
        </>
      )}
      <ChatMessageFooter>
        <span>{message.time}</span>
        {!isOpponent && (
          <Icon
            id={`${message.status === "SENT" ? "singleTick" : "doubleTick"}`}
            className={`chat__msg-status-icon ${
              message.status === "READ" ? "chat__msg-status-icon--blue" : ""
            }`}
          />
        )}
      </ChatMessageFooter>
    </ChatMessage>
  );
});
